'use client';

import { useEffect, useRef, useState } from 'react';
import { tw } from '@tw';
import { EYES, EYE_SIZE, FACE_TRANSFORM, MATES, SMILE, TILE, type Mate } from '@/lib/brand/team';

/** Longest a stop waits for the spin to land: the fold's spin delay plus one spin. */
const LAND_MAX_MS = 2_100;

/**
 * A teammate's face, or the Meetmy mark when given `ink` and `light` (the site bar's).
 * `working` plays the landing's hand-off turn for as long as the work lasts: the logo folds into three dots that spin, and once the work is done the spin
 * finishes its turn and the dots unfold back into the logo, as they do on the main page.
 */
export function MateFace({
  mate = 0,
  ink,
  light = '#ffffff',
  edge,
  working = false,
  quick = false,
  className,
}: {
  mate?: Mate;
  ink?: string;
  light?: string;
  /** An outline round the tile, for a fill too pale to hold its shape on a white page. */
  edge?: string;
  working?: boolean;
  /** Skip the landing's opening hold, for a face that answers a tap or a scroll. */
  quick?: boolean;
  className?: string;
}) {
  const [phase, setPhase] = useState<'rest' | 'work' | 'unfold'>('rest');
  const stopping = useRef(false);

  useEffect(() => {
    if (working) {
      stopping.current = false;
      // A frame late, so a card that appears already working shows the logo first.
      const frame = requestAnimationFrame(() => setPhase('work'));
      return () => cancelAnimationFrame(frame);
    }
    // The spin lands on a whole turn at the end of each round (`onAnimationIteration`); with
    // reduced motion there is no spin to wait for, and the timer covers a spin that never began.
    stopping.current = true;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const land = window.setTimeout(
      () => setPhase((p) => (p === 'work' ? 'unfold' : p)),
      still ? 0 : LAND_MAX_MS
    );
    return () => window.clearTimeout(land);
  }, [working]);

  return (
    <svg
      aria-hidden
      viewBox="0 0 200 200"
      data-face={phase}
      data-quick={quick || undefined}
      className={tw('mm-face shrink-0', edge && 'overflow-visible', className)}
    >
      <path
        d={TILE}
        fill={ink ?? MATES[mate].color}
        stroke={edge}
        strokeWidth={edge ? 6 : undefined}
      />
      <g transform={FACE_TRANSFORM} fill={light}>
        <g
          className="mm-face-spin"
          onAnimationIteration={() => {
            if (stopping.current) setPhase('unfold');
          }}
        >
          {EYES.map((eye, i) => (
            <g key={eye.x} className={i === 0 ? 'mm-face-l' : 'mm-face-r'}>
              <rect x={eye.x} y={eye.y} width={EYE_SIZE} height={EYE_SIZE} rx="4" />
            </g>
          ))}
          <path
            d={SMILE}
            className="mm-face-s"
            fill="none"
            stroke={light}
            strokeWidth="9"
            strokeLinecap="round"
          />
          {/* Safari's smile dot (`site-theme.css`), hidden wherever the arc can reshape itself. */}
          <circle cx="74.05" cy="115" r="6.5" className="mm-face-dot" />
        </g>
      </g>
    </svg>
  );
}
